import React, { FC } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../../../../../types';
import type { ScoreboardCellsProps } from '../../types';
import type { ReduxState } from 'app/redux/types';

import { withinBettingContext } from 'app/contexts/betting';
import { useTranslation } from 'app/lang';
import {
  type ScoresEntityByTeamId,
  type ScoresPartOfGameEntity,
  TennisPeriodType,
  TennisScoreType,
} from 'app/lib/betting/betting-entities';
import { TeamId, TEAMS } from 'app/lib/betting/constants';
import { TennisTieBreakTypes } from 'app/lib/periods/Tennis/constants';

import MainScoreWrapper from '../MainScoreWrapper';
import { ScoreboardCell, ScoreboardCellWrapper } from '../ScoreboardCell';
import { formatTennisGameScores } from './utils';

import dictionary from './Tennis.dictionary.json';

type StoreProps = {
  setsScores: ScoresPartOfGameEntity;
  currentGameScores: ScoresEntityByTeamId | null;
  isPeriodFinished: boolean;
  servingTeamId: TeamId;
  tieBreak: TennisTieBreakTypes;
  gameNumber: number;
};

const {
  POINT,
} = TennisScoreType;
const {
  GAME,
  SET,
} = TennisPeriodType;

const TennisScoreboardCellsWithoutHOC: FC<ScoreboardCellsProps & StoreProps> = ({
  setsScores,
  currentGameScores,
  isPeriodFinished,
  servingTeamId,
  tieBreak,
  gameNumber,
  matchId,
}) => {
  const { t } = useTranslation();
  const cells = [];
  const isTieBreak = tieBreak === TennisTieBreakTypes.TIE_BREAK;
  const isBigHelmet = tieBreak === TennisTieBreakTypes.SUPER_TIE_BREAK_BIG_HELMET;
  const isSuperTieBreak = tieBreak === TennisTieBreakTypes.SUPER_TIE_BREAK_IN_LAST_SET;

  if (setsScores !== null) {
    const currentSetScores = setsScores.last();

    setsScores.forEach((scores, setNumber) => {
      const current = scores === currentSetScores && !isPeriodFinished;
      const isGameNumber = setsScores.size === setNumber && !isPeriodFinished && gameNumber;
      const isSuperTieBreakTitle = (isBigHelmet || isSuperTieBreak) && current;

      const cellWrapperClassName = classNames('set-wrapper', {
        'game-wrapper': isGameNumber,
        'current-period-score': current,
      });

      const setTitle = t(dictionary.setTitle, { setNumber });
      const gameTitle = isGameNumber && gameNumber ? t(dictionary.gameTitle, { gameNumber }) : '';
      const secondPartOfContent = isTieBreak && current ? t(dictionary.tieBreakTitle) : gameTitle;

      const content = isSuperTieBreakTitle
        ? t(dictionary.superTieBreakTitle)
        : classNames(setTitle, secondPartOfContent);

      const Sets = (
        TEAMS.map((teamId) => (
          <ScoreboardCell key={teamId}>
            {(isSuperTieBreak && current)
              ? currentGameScores?.get(teamId, '–')
              : scores.get(teamId, '–')}
          </ScoreboardCell>
        ))
      );

      cells.push(
        <ScoreboardCellWrapper content={content}
                               className={cellWrapperClassName}
                               key={setNumber}>
          {isGameNumber ? (
            <div>
              {Sets}
            </div>
          ) : Sets}

          {isGameNumber && currentGameScores && !isSuperTieBreak && (
            <div className="active">
              {TEAMS.map((teamId) => {
                const scoresTeamId = (
                  (isTieBreak || isBigHelmet)
                    ? currentGameScores?.get(teamId, '–')
                    : formatTennisGameScores(currentGameScores).get(teamId, '–')
                );
                return (
                  <ScoreboardCell key={teamId}>
                    {teamId === servingTeamId
                      ? `*${scoresTeamId}`
                      : scoresTeamId
                    }
                  </ScoreboardCell>
                );
              })}
            </div>
          )}
        </ScoreboardCellWrapper>,
      );
    });
  }

  cells.push(
    <MainScoreWrapper matchId={matchId}
                      key="ms" />,
  );

  return <>{cells}</>;
};

const mapStateToProps = (state: ReduxState, {
  $betting: {
    content: {
      selectors: {
        matches,
      },
    },
  },
  matchId,
}: BettingContextProps): StoreProps => {
  const match = matches(state).get(matchId);
  const scores = match.scores;
  const periods = match.periods;
  const isMatchFinished = periods?.get(GAME)?.finished;
  const isPeriodFinished = periods?.get(POINT)?.finished;

  return {
    setsScores: scores.getIn([POINT, SET], null),
    currentGameScores: !isMatchFinished ? scores.getIn([POINT, GAME], null)?.last() : null,
    servingTeamId: !isPeriodFinished ? match.getIn(['state', 'serveTeam']) : null,
    tieBreak: match.getIn(['state', 'tieBreak']),
    isPeriodFinished,
    gameNumber: periods?.get(GAME)?.number,
  };
};

export const TennisScoreboardCells = compose<FC<ScoreboardCellsProps>>(
  withinBettingContext,
  connect(mapStateToProps),
)(TennisScoreboardCellsWithoutHOC);
